import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';

interface Category {
  id: string;
  name: string;
  type: string;
  icon?: string;
  color?: string;
  parent_id?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  count?: number;
}

interface CreateCategoryData {
  name: string;
  type: string;
  icon?: string;
  color?: string;
  parent_id?: string;
}

interface UpdateCategoryData {
  name?: string;
  icon?: string;
  color?: string;
  parent_id?: string;
}

export const useCategories = (type?: string) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (type) params.append('type', type);

      const result = await apiClient.request<{ categories: Category[] }>(
        `/api/categories${params.toString() ? `?${params.toString()}` : ''}`
      );

      if (result.error) {
        setError(result.error.message);
      } else if (result.data) {
        setCategories(result.data.categories);
      }
    } catch (err) {
      setError('Failed to fetch categories');
      console.error('Fetch categories error:', err);
    } finally {
      setLoading(false);
    }
  }, [type]);

  // Create category
  const createCategory = useCallback(async (data: CreateCategoryData) => {
    setError(null);

    try {
      const result = await apiClient.request<{ category: Category }>(
        '/api/categories',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );

      if (result.error) {
        setError(result.error.message);
        return null;
      }

      if (result.data) {
        // Add new category to the list
        setCategories(prev => [...prev, result.data!.category]);
        return result.data.category;
      }

      return null;
    } catch (err) {
      setError('Failed to create category');
      console.error('Create category error:', err);
      return null;
    }
  }, []);

  // Update category
  const updateCategory = useCallback(async (id: string, data: UpdateCategoryData) => {
    setError(null);

    try {
      const result = await apiClient.request<{ category: Category }>(
        `/api/categories/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      );

      if (result.error) {
        setError(result.error.message);
        return null;
      }

      if (result.data) {
        // Update category in the list
        setCategories(prev =>
          prev.map(cat => (cat.id === id ? result.data!.category : cat))
        );
        return result.data.category;
      }

      return null;
    } catch (err) {
      setError('Failed to update category');
      console.error('Update category error:', err);
      return null;
    }
  }, []);

  // Delete category
  const deleteCategory = useCallback(async (id: string) => {
    setError(null);

    try {
      const result = await apiClient.request<{ message: string }>(
        `/api/categories/${id}`,
        {
          method: 'DELETE',
        }
      );

      if (result.error) {
        setError(result.error.message);
        return false;
      }

      // Remove category from the list
      setCategories(prev => prev.filter(cat => cat.id !== id));
      return true;
    } catch (err) {
      setError('Failed to delete category');
      console.error('Delete category error:', err);
      return false;
    }
  }, []);

  // Get category stats
  const getCategoryStats = useCallback(async (id: string) => {
    try {
      const result = await apiClient.request<{ stats: { count: number } }>(
        `/api/categories/${id}/stats`
      );

      if (result.error) {
        console.error('Get category stats error:', result.error);
        return null;
      }

      return result.data?.stats || null;
    } catch (err) {
      console.error('Get category stats error:', err);
      return null;
    }
  }, []);

  // Refresh categories (alias for fetchCategories)
  const refresh = fetchCategories;

  // Load categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryStats,
    refresh,
  };
};
